import { Flex, Skeleton, Spin } from 'antd';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { BaseButton } from 'components/button/button-base';
import { ErrorDisplay, ErrorListDisplay } from 'components/ErrorDisplay';
import { BaseHeader } from 'components/header';
import { Text } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { QAPanelTemplate } from 'features/patient/assessment/assessment-controller/_components/questionnaire-qa-panel';
import { ConcussionRXLogo } from 'features/sign-in/v2/concussionRx-logo';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { useGetBasicClinicInfo } from 'lib/hooks/tanstack/use-getClinicInfo';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useErrorList } from 'lib/utils/useErrorList';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import OrganisationRegistrationFormPage from '../_components/organisation_registration_form_page';
import { OrganisationOnboardingBillingSetupPage } from './page-organisation-onboarding-billingSetup';
import { OrganisationOnboardingCompletionPage } from './page-organisation-onboarding-completed';
import RegistrationSteps from '../_components/RegistrationSteps';
import { useOnboardingReducer } from '../_hooks/useOnboardingReducer';

interface OnboardingSetupContentProps {
  organisationId: string;
}

function OnboardingSetupContent(props: OnboardingSetupContentProps) {
  const history = useHistory();
  const [onboardingState, dispatchOnboarding] = useOnboardingReducer();
  const errorList = useErrorList();

  const clinicInfoQuery = useGetBasicClinicInfo(props.organisationId);
  useQueryErrorNotifier(clinicInfoQuery, errorList);

  useEffect(() => {
    if (clinicInfoQuery.data?.initialised) {
      history.push('/');
    }
  }, [clinicInfoQuery.data, history]);

  // Loading state
  if (clinicInfoQuery.isLoading) {
    return <Skeleton active />;
  }

  // Error display for already onboarded or other errors
  if (errorList.hasErrors) {
    return (
      <PrintableRootPage>
        <BaseHeader
          headerStyle={{
            background: 'none'
          }}
        />
        <Flex align="center" justify="center">
          <ConcussionRXLogo
            style={{ width: 'revert', height: '50px', marginBottom: '20px' }}
          />
        </Flex>
        <Flex vertical gap={16}>
          <ErrorListDisplay errorList={errorList} />

          <div>
            <BaseButton
              style={{ display: 'flex', width: 'min-content' }}
              href={'/'}
            >
              Navigate to the home page
            </BaseButton>
          </div>
        </Flex>
      </PrintableRootPage>
    );
  }

  function renderStep() {
    switch (onboardingState.currentPage) {
      case 1:
        return (
          <OrganisationRegistrationFormPage
            organisationId={props.organisationId}
            // eslint-disable-next-line
            goToNextStep={(_organisationId: string) => {
              // TEMPORARY: Skip billing setup
              dispatchOnboarding({ type: 'MOVE_TO_PAGE_3' });
            }}
          />
        );
      case 2:
        return (
          <>
            {onboardingState.organisationId && (
              <OrganisationOnboardingBillingSetupPage
                organisationId={onboardingState.organisationId}
                goToNextStep={() =>
                  dispatchOnboarding({ type: 'MOVE_TO_PAGE_3' })
                }
              />
            )}
            {!onboardingState.organisationId && (
              <div>
                <Spin />
                <Text>Waiting for the organisation ID to be set</Text>
              </div>
            )}
          </>
        );
      case 3:
        return <OrganisationOnboardingCompletionPage orgId={props.organisationId} />;
      default:
        return <div>Unknown Step</div>;
    }
  }

  return (
    <PrintableRootPage>
      <QAPanelTemplate>
        <BaseButton
          onClick={() => {
            dispatchOnboarding({ type: 'MOVE_TO_PAGE_1' });
          }}
        >
          Page 1
        </BaseButton>
        <BaseButton
          onClick={() => {
            dispatchOnboarding({
              type: 'MOVE_TO_PAGE_2',
              organisationId: 'fake-clinic'
            });
          }}
        >
          Page 2
        </BaseButton>
      </QAPanelTemplate>
      <BaseHeader
        headerStyle={{
          background: 'none'
        }}
      />

      <Flex align="center" justify="center">
        <ConcussionRXLogo
          style={{ width: 'revert', height: '50px', marginBottom: '20px' }}
        />
      </Flex>
      <RegistrationSteps current={onboardingState.currentPage} />
      {renderStep()}
    </PrintableRootPage>
  );
}

export default function OnboardingSetupPage() {
  const auth = useAuth();
  const { orgId } = useParams<{ orgId: string }>();

  // Loading state
  if (auth.isUserDataLoading) {
    return <Skeleton active />;
  }

  if (!orgId) {
    return (
      <PrintableRootPage>
        <BaseHeader
          headerStyle={{
            background: 'none'
          }}
        />
        <Flex align="center" justify="center">
          <ConcussionRXLogo
            style={{ width: 'revert', height: '50px', marginBottom: '20px' }}
          />
        </Flex>
        <Flex>
          <Text>No organisation ID was detected in the URL.</Text>
          <ErrorDisplay
            title="No organisation ID was detected"
            description={
              <>
                Please report this error to{' '}
                <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>{CRX_SUPPORT_EMAIL}</a>
              </>
            }
            error={{ message: `No ID` }}
          />
        </Flex>
      </PrintableRootPage>
    );
  }

  return <OnboardingSetupContent organisationId={orgId} />;
}
